<template>
    <div class="space-y-6">
        <form
            v-if="token === undefined"
            class="space-y-4"
            @submit="submitPhone"
        >
            <div class="font-medium text-gray-600">
                {{ t('checkout.phoneVerificationPrompt') }}
            </div>

            <login-phone-field
                ref="phoneFieldRef"
                :field-bind="phoneValue"
                :disabled="isPending"
                :error="displayedError"
            />

            <div
                v-if="formError !== undefined"
                class="text-red-600 font-medium"
            >
                {{ formError }}
            </div>

            <div class="flex w-full flex-col gap-2 sm:flex-row">
                <prime-button
                    :label="t('cancel')"
                    class="flex-1"
                    outlined
                    :disabled="isPending"
                    @click="cancel"
                />
                <prime-button
                    :label="t('sendVerificationCode')"
                    type="submit"
                    class="flex-1"
                    :loading="isPending"
                />
            </div>

            <div class="text-sm text-gray-700">
                <i18n-t
                    keypath="otpDisclaimer"
                    scope="global"
                    tag="span"
                >
                    <template #sendVerificationCode>
                        <span class="italic">
                            {{ t('sendVerificationCode') }}
                        </span>
                    </template>
                    <template #terms>
                        <a
                            class="text-primary-600 underline hover:no-underline"
                            href="https://www.redpointhq.com/terms-of-service-sms/"
                            >{{ t('terms') }}</a
                        >
                    </template>
                    <template #privacyPolicy>
                        <a
                            class="text-primary-600 underline hover:no-underline"
                            href="https://www.redpointhq.com/privacy-policy/"
                            >{{ t('privacyPolicy') }}</a
                        >
                    </template>
                </i18n-t>
            </div>
        </form>

        <div
            v-else
            class="space-y-4"
        >
            <div>
                <span class="font-medium text-gray-600">{{
                    t('codeEnter')
                }}</span>
                <div class="mt-1 flex items-center">
                    <i class="pi pi-phone text-gray-600"></i>
                    <span class="text-900 ml-2 font-bold">{{
                        phoneLabel
                    }}</span>
                </div>
            </div>

            <div ref="codeRef">
                <input-text
                    v-model="code"
                    :placeholder="t('signInFlow.verificationCodePlaceholder')"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    class="!text-[16px]"
                    @keydown.enter="finish"
                />
                <div
                    v-if="codeError !== undefined"
                    class="text-red-600 font-medium"
                >
                    {{ codeError }}
                </div>
            </div>

            <div class="flex w-full flex-col gap-2 sm:flex-row">
                <prime-button
                    :label="t('goBack')"
                    class="flex-1"
                    icon="pi pi-arrow-left"
                    outlined
                    @click="goBack"
                />
                <prime-button
                    :label="t('codeVerify')"
                    class="flex-1"
                    @click="finish"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { object, string, boolean } from 'yup'
import { toTypedSchema } from '@vee-validate/yup'
import { computed, inject, nextTick, onMounted, type Ref, ref } from 'vue'
import { useForm } from 'vee-validate'
import { useMutation } from '@tanstack/vue-query'
import PrimeButton from 'primevue/button'
import InputText from 'primevue/inputtext'
import LoginPhoneField from '~/components/Verification/LoginPhoneField.vue'
import { graphql } from '~/resources/graphql'
import { useRpQuery } from '~/composables/graphql'
import type {
    CheckoutPhoneVerificationInput,
    SendCheckoutPhoneVerificationCodeInput
} from '~/resources/graphql/graphql'
import { I18nT, useI18n } from 'vue-i18n'

type CheckoutPhoneVerificationModalResult = CheckoutPhoneVerificationInput & {
    phoneLabel: string
}

const emit = defineEmits<{
    (e: 'done', payload: CheckoutPhoneVerificationModalResult): void
    (e: 'cancel'): void
}>()

const dialogRef = inject<
    Ref<{
        data: {
            initialMessage?: string
            initialToken?: string
            initialPhoneLabel?: string
        }
    }>
>(
    'dialogRef',
    ref({
        data: {}
    })
)

const { t } = useI18n({ useScope: 'global' })
const initialMessage = computed(() => dialogRef.value.data.initialMessage)
const initialToken = computed(() => dialogRef.value.data.initialToken)
const token = ref<string | undefined>(initialToken.value)
const phoneLabel = ref(dialogRef.value.data.initialPhoneLabel ?? '')
const code = ref('')
const codeError = ref<string | undefined>(
    initialToken.value === undefined ? undefined : initialMessage.value
)
const formError = ref<string | undefined>(
    initialToken.value === undefined ? initialMessage.value : undefined
)
const displayedError = ref<string>()
const phoneFieldRef = ref<InstanceType<typeof LoginPhoneField> | null>(null)
const codeRef = ref<HTMLDivElement>()

const schema = toTypedSchema(
    object({
        phone: object()
            .shape({
                formatted: string(),
                number: string(),
                valid: boolean()
            })
            .test({
                message: t('customerInformation.invalidMobilePhoneWarning'),
                test: value =>
                    value?.number !== undefined &&
                    value.number !== '' &&
                    value.valid === true
            })
    })
)

const { defineField, handleSubmit } = useForm({
    validationSchema: schema
})

const [phone, phoneProps] = defineField('phone', {
    validateOnModelUpdate: false,
    validateOnChange: false,
    validateOnInput: false,
    validateOnBlur: true
})

const phoneValue = computed(() => ({
    // `defineField` replaces the deprecated component binds API, so this keeps
    // LoginPhoneField on the same component v-model contract it already expects.
    ...phoneProps.value,
    modelValue: phone.value,
    'onUpdate:modelValue': (value: (typeof phone)['value']) => {
        phone.value = value
    }
}))

onMounted(() => {
    if (token.value !== undefined) {
        nextTick(() => {
            codeRef.value?.querySelector('input')?.focus?.()
        })
    }
})

const query = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: (input: SendCheckoutPhoneVerificationCodeInput) =>
        query(
            graphql(/** @lang GraphQL */ `
                mutation SendCheckoutPhoneVerificationCode(
                    $input: SendCheckoutPhoneVerificationCodeInput!
                ) {
                    sendCheckoutPhoneVerificationCode(input: $input) {
                        __typename
                        ... on SendCheckoutPhoneVerificationCodeResult {
                            token
                        }
                        ... on RateLimitProblem {
                            message
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            { input }
        )
})

const submitPhone = handleSubmit(values => {
    displayedError.value = undefined
    formError.value = undefined
    const phoneNumber = values.phone.number ?? ''

    mutate(
        {
            phone: phoneNumber
        },
        {
            onSuccess: data => {
                const result = data.sendCheckoutPhoneVerificationCode
                if (
                    result.__typename ===
                    'SendCheckoutPhoneVerificationCodeResult'
                ) {
                    token.value = result.token
                    phoneLabel.value = values.phone.formatted || phoneNumber
                    codeError.value = undefined
                    nextTick(() => {
                        codeRef.value?.querySelector('input')?.focus?.()
                    })
                    return
                }

                if (result.__typename === 'RateLimitProblem') {
                    formError.value = result.message
                    return
                }

                if (result.__typename === 'ValidationError') {
                    displayedError.value =
                        result.fieldErrors[0]?.message ??
                        t('customerInformation.invalidMobilePhoneWarning')
                    return
                }

                formError.value = t('somethingWrongWarning')
            }
        }
    )
})

function finish() {
    if (token.value === undefined) {
        return
    }

    if (!/^\d{6}$/.test(code.value)) {
        codeError.value = t('codeInvalid')
        codeRef.value?.querySelector('input')?.select?.()
        return
    }

    emit('done', {
        token: token.value,
        code: code.value,
        phoneLabel: phoneLabel.value
    })
}

function goBack() {
    token.value = undefined
    code.value = ''
    codeError.value = undefined
    formError.value = undefined
    nextTick(() => phoneFieldRef.value?.focus())
}

function cancel() {
    emit('cancel')
}
</script>
